/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../lib/@waca/core-client/js/core-client/ui/core/View',
	'jquery',
	'underscore',
	'../lib/@waca/dashboard-common/dist/ui/CenterLoadingSpinner',
	'./PinsPanelView',
	'react',
	'react-dom'
], function (
	BaseView,
	$,
	_,
	CenterLoadingSpinner,
	PinsPanelView,
	React,
	ReactDOM
) {

	/**
	 * The Panel which shows the pinned content
	 */
	const View = BaseView.extend({

		events: {
			'mousedown .pinItem>div:not(.prop-deleteButton-item)': 'onDragStart',
			'mouseup .pinItem>div:not(.prop-deleteButton-item)': 'onMouseUp'
		},

		init: function (attributes) {
			View.inherited('init', this, arguments);
			this.ajaxSvc = attributes.ajaxSvc;
			this.glassContext = attributes.glassContext;
			this.canvasController = attributes.canvasController;
			this.dashboardApi = this.canvasController.dashboardApi;
			this.logger = this.glassContext.getCoreSvc('.Logger');

			this.dndManager = attributes.dndManager;
			this.dashboardPinningService = attributes.dashboardPinningService;
			this.smartNamingSvc = attributes.smartNamingSvc;

			this.viewMode = attributes.viewMode;
			this.dateFilterString = 'all';

			if (!this.dashboardPinningService ) {
				throw new Error('Pinning service not provided to PinsPanel constructor');
			}
		},

		/**
		 * Called by the DnD manager to check if this drop zone accept the dragged object
		 *
		 * @returns {Boolean}
		 */
		accepts: function (dragObject) {
			return dragObject.type === 'pin';
		},

		/**
		 * Handles dragging action.
		 * The function sends the id of a pin to pinning service and calls the dnd manager with the pin spec result
		 *
		 * @public
		 *
		 * @param  {object} event - The event to process
		 */
		onDragStart: function (event) {
			this._mouseUp = false;
			if (this.dashboardApi.getMode() !== this.dashboardApi.MODES.EDIT) {
				return Promise.resolve(true);
			}
			var pinEl = $(event.currentTarget).parents('.pinItem')[0];

			var id = pinEl.getAttribute('data-id');
			if (id) {
				return this.getPin(id)
					.then((pinSpec) => {
						// If the mouse is up before we start dragging then just return
						if (this._mouseUp) {
							return;
						}
						pinSpec = JSON.parse(JSON.stringify(pinSpec));
						if (!_.isEmpty(pinSpec) && !this._stoppedDrag) {
							this.dndManager.startDrag({
								event: event,
								type: 'pin',
								data: this._buildDropInfo(pinSpec),
								avatar: this._buildAvatar(pinSpec),
								moveXThreshold: 20,
								moveYThreshold: 20
							});
						}
					});
			} else {
				return Promise.resolve();
			}

		},

		// User might of single clicked, so stop the drag from starting
		onMouseUp: function () {
			this._mouseUp = true;
		},

		/**
		 * Gets a pin spec from calling glassContext using a pin id
		 *
		 * @public
		 *
		 * @param {stirng} id - The id of a pin
		 * @return {string} pinSpec - The spec of a pin
		 */
		getPin: function (id) {
			return this.dashboardPinningService.getPin(id);
		},

		/**
		 * A helper function to build avatar for a pin
		 * @param  {string} pinSpec - the spec of a pin
		 * @return {object} - a jquery object with an img of the pin if present.
		 */
		_buildAvatar: function (pinSpec) {
			var avatar = $('<img/>', {
				class: 'avatar pin'
			});
			avatar.attr('src', pinSpec.thumbUri);
			return avatar;
		},

		/**
		 * a helper method to build drop info based on the pin spec that was received from the server
		 *
		 * @private
		 *
		 * @param  {object} pinSpec - description of a pin
		 * @return {object} dropInfo
		 */
		_buildDropInfo: function (pinSpec) {
			return {
				operation: 'new',
				pinSpec: pinSpec
			};
		},


		/**
		 * primary action handler for adding pin to canvas
		 *
		 * @public
		 * @param {string} id - id of a pin to add to canvas 
		 * @param {event} event - The event object to process
		 */
		onKeyDownToCreatePin: function (pinId, event) {
			// get selected pin
			if (pinId) {
				return this.getPin(pinId).then(function (pinSpec) {
					pinSpec = JSON.parse(JSON.stringify(pinSpec)); //Clone the pin spec, as the add operation is side-effecting, and we don't want any updates to change the cached pin.
					if (!_.isEmpty(pinSpec)) {
						let fragmentModel = {
							layout: pinSpec.content.layout,
							widgets: pinSpec.content.widgets,
							dataSources: pinSpec.content.dataSources,
							version: pinSpec.version ? pinSpec.content.specVersion : 6,
							sourceName: pinSpec.sourceName
						};
						if (pinSpec.content.episodes) {
							fragmentModel.episodes = pinSpec.content.episodes;
						}
						let options = {
							model: fragmentModel
						};
						if (pinSpec.content.layout && pinSpec.content.layout.style) {
							options.layoutProperties = {
								style: _.omit(pinSpec.content.layout.style, 'top', 'left')
							};
						}
						const isTouch = event.type === 'tap';
						this._addPinToLayout(options, isTouch);
					}
				}.bind(this));
			} else {
				return Promise.resolve();
			}
		},

		_addPinToLayout: function(options, isTouch) {
			this.canvasController.addPin(options, isTouch);
		},

		render: function() {
			return this._renderSpinner().then(() => {
				this.dndManager.addDropTarget(this.el, {
					accepts: this.accepts
				});
				ReactDOM.render(
					<PinsPanelView
						addPinToCanvas={(pinId, event) => { this.onKeyDownToCreatePin(pinId, event);}}
						smartNamingSvc={this.smartNamingSvc}
						dashboardPinningService={this.dashboardPinningService}
					/>, this.$el[0]);
			});
		},

		_renderSpinner(){
			const spinnerContainer = document.createElement('div');
			this.$el.append(spinnerContainer);
			const spinnerOptions = { variant: 'circle', size: 'normal' };
			ReactDOM.render(CenterLoadingSpinner(spinnerOptions), spinnerContainer);
			return this.dashboardPinningService.getPins(this.dateFilterString).then(() => ReactDOM.unmountComponentAtNode(spinnerContainer));
		},

		remove() {
			ReactDOM.unmountComponentAtNode(this.$el[0]);
			View.inherited('remove', this, arguments);
		}
	});

	return View;
});
